import { QueryDataState } from '@shared/interfaces';
import { LowerRole } from '@shared/types';

import { BugoBrand } from '../bugoBrand/bugoBrand.interface';
import {
  GiftCouponForStore,
  GiftCouponSummary,
} from '../giftCoupon/giftCoupon.interface';
import {
  GiftDeliveryForStore,
  GiftDeliverySummary,
} from '../giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '../giftItem/giftItem.interface';
import { Image } from '../media/media.interface';

export enum GiftOrderItemType {
  Delivery = 'Delivery', // 배송형
  Coupon = 'Coupon', // 쿠폰형
}

export enum GiftOrderDeliveryStatus {
  BeforePreparing = 'BeforePreparing',
  Preparing = 'Preparing',
  Deliverying = 'Deliverying',
  Delivered = 'Delivered',
}

export enum GiftOrderPaymentStatus {
  NotPaid = 'NotPaid',
  Waiting = 'Waiting',
  Paid = 'Paid',
  Confirmed = 'Confirmed',
  CancelRequested = 'CancelRequested',
  Canceled = 'Canceled',
}

export enum GiftOrderDeliveryMode {
  Individual = 'Individual', // 개별배송 (받는 사람이 각각 배송지 입력)
  Bulk = 'Bulk', // 일괄배송 (상주가 한 곳으로 수령)
}

export enum GiftOrderDocStatus {
  Active = 'Active',
  Deleted = 'Deleted',
}

// 추가교환 추가결제 원장 항목 (GiftOrder.extraPayments[])
export interface GiftOrderExtraPayment {
  coupon: string; // 스폰 쿠폰 _id
  amount: number;
  taxFreeAmount?: number;
  taxableAmount?: number;
  method?: string;
  receiptId?: string;
  isPaid: boolean;
  paidAt?: Date;
  paidBy?: string;
}

// 추가교환 충당용 빌링카드 (GET /admin/gift-order/:id/billing-card 응답)
export interface GiftOrderBillingCard {
  cardCompany: string;
  cardNumber: string;
  expiry: string; // "MM/YY"
  registeredAt?: Date;
  registeredBy?: { name: string; email: string };
}

// 빌링카드 등록 DTO (PATCH /admin/gift-order/:id/billing-card)
export interface SetBillingCardDto {
  cardCompany: string;
  cardNumber: string;
  expiry: string; // "MM/YY"
}

// 개별 주문 상품 인터페이스
export interface GiftOrderItem<TGiftItem = string, TDeliveredImage = string> {
  giftItem: TGiftItem;
  giftItemEmbed: {
    name: string;
    priceRetail: number;
    priceRegular: number;
    priceWhole: number;
    priceRebate: number;
    isTaxable: boolean;
    /** 구매 당시 상조 수수료율(%) 스냅샷 — FeeRate 상조만, 2026-07-24 이후 주문 (레거시엔 없음) */
    feeRate?: number;
    /**
     * 구매 시점 동결 단가당 수수료(원) = ceilFeeWon(priceRetail × feeRate/100), 1원 올림 정수.
     * 정산은 이 값을 그대로 차감(판매가 − feePerUnit − 도매가) — 요율 변경은 이후 구매분부터만.
     */
    feePerUnit?: number;
  };
  quantity: number;
  type: GiftOrderItemType;
  selectedOptions?: GiftSelectedOption[]; // 추가옵션 선택값 스냅샷 (있을 때만)
  // 배송형일 때 사용
  deliveryDetail?: {
    status: GiftOrderDeliveryStatus;
    deliveredAt?: Date;
    estimatedDeliveredAt?: Date;
    receiverAddress: string;
    receiverPostalCode: string;
    deliveredImage?: TDeliveredImage;
  };
}

export type GiftOrder<
  TUser = string,
  TFevent = string,
  TBugo = string,
  TBugoBrand = string,
  TGiftItem = string,
  TDeliveredImage = string,
  TCoupon = unknown,
  TDelivery = unknown,
> = {
  _id: string;
  user: TUser;
  fevent: TFevent;
  bugo: TBugo;
  bugoBrand: TBugoBrand;
  docStatus: GiftOrderDocStatus;
  orderDetails: GiftOrderItem<TGiftItem, TDeliveredImage>[];
  paymentDetail: {
    status: GiftOrderPaymentStatus;
    method: string;
    receiptId: string;
    receiptUrl: string;
    cancelId: string;
    paymentByCash: number;
    taxFreeAmount?: number;
    taxableAmount?: number;
    vatAmount?: number;
    paymentByPoint: number;
    isAccounted?: boolean;
    accountedDate?: Date;
    isRebated?: boolean;
    rebatedDate?: Date;
    requestedAt?: Date;
    purchasedAt?: Date;
    canceledAt?: Date;
    cancelReason?: string;
    cancelledTotalPrice?: number;
    isAbnormalPayment?: boolean;
    vBank?: string;
    // 가상계좌/입금 수기환불 취소 기록 (append; spec §7). 조회용 옵셔널.
    manualRefunds?: GiftOrderManualRefund[];
  };
  receiverName?: string;
  receiverPhoneNumber?: string;
  // 답례글 (전달 페이지에서 저장)
  courtesyContent?: string;
  // 추가교환 충당용 빌링카드 (select:false → 전용 엔드포인트로만 조회)
  billingCard?: GiftOrderBillingCard;
  // 추가교환 추가결제 원장
  extraPayments?: GiftOrderExtraPayment[];
  deliveryMode?: GiftOrderDeliveryMode;
  // Virtual fields
  isMultiOrder: boolean;
  coupons?: TCoupon[];
  deliveries?: TDelivery[];
  createdAt: Date;
  updatedAt: Date;
};

// 스토어에서 사용하는 타입
export type GiftOrderForStore = GiftOrder<
  string,
  string,
  string,
  string,
  GiftItemForStore,
  Image,
  GiftCouponForStore,
  GiftDeliveryForStore
>;

export type GiftOrderForStoreState = QueryDataState<GiftOrderForStore>;

export type GiftOrderListForStore = GiftOrderForStore[];

export type GiftOrderListForStoreState = QueryDataState<GiftOrderListForStore>;

// Admin용 타입 (populate된 데이터)
export interface GiftOrderForAdminBugo {
  _id: string;
  member?: {
    _id: string;
    fullName: string;
    phoneNumber: string;
  };
  fevent?: {
    _id: string;
    deceasedInfo?: {
      name: string;
    };
  };
}

export type GiftOrderForAdmin = GiftOrder<
  { _id: string; info?: { name?: string; phoneNumber?: string } }, // user
  { _id: string; deceasedInfo?: { name?: string } }, // fevent
  GiftOrderForAdminBugo, // bugo
  BugoBrand, // bugoBrand
  GiftItemForStore, // giftItem
  Image, // deliveredImage
  GiftCouponSummary, // coupons (virtual)
  GiftDeliverySummary // deliveries (virtual)
>;

export type GiftOrderListForAdmin = GiftOrderForAdmin[];

// 개별 주문 상품 생성 DTO
// 주문 시 담는 추가옵션 선택값 스냅샷
export interface GiftSelectedOption {
  optionName: string; // AdditionalOption.name
  slotIndex?: number; // selectionCount>1일 때 슬롯 번호(0-based). 같은 optionName이 슬롯 수만큼 반복됨
  choiceName: string; // 선택한 OptionChoice.name
  priceDelta: number; // 선택한 OptionChoice.priceDelta (선택 시점 값)
}

export interface GiftOrderItemCreateDto {
  giftItem: string; // giftItem._id
  quantity: number;
  selectedOptions?: GiftSelectedOption[]; // 추가옵션 선택값 (있을 때만)
  couponDetail?: { totalCount?: number }; // 쿠폰형일 때만
}

// 주문당 1건 — 감사메시지 인쇄 (인쇄 브랜드 상품 포함 시에만 전송)
export interface GiftThanksPrintDto {
  templateId: string; // 선택한 ThanksPrintTemplate._id
  message: string; // 인쇄용 감사글
  senderName: string; // 보내는 이 (발신표현 포함, 예: "홍길동 배상")
}

// Bulk 배송지 정보
export interface BulkDeliveryAddress {
  name: string; // 받는분 이름
  phoneNumber: string; // 받는분 전화번호
  address: string; // 기본주소 (우편번호 검색 결과)
  addressDetail?: string; // 상세주소 (동/호수 등)
  postalCode: string; // 우편번호
}

// GiftOrder 생성 DTO (다중 상품 지원)
export interface GiftOrderCreateDto {
  bugo: string; // bugo._id
  receiverName: string; // 주문자 이름
  receiverPhoneNumber: string; // 주문자 연락처
  deliveryMode?: GiftOrderDeliveryMode; // 배송형 상품 수령 방식
  bulkDeliveryAddress?: BulkDeliveryAddress; // Bulk 모드일 때 배송지 정보
  orderDetails: GiftOrderItemCreateDto[];
  thanksPrint?: GiftThanksPrintDto; // 감사메시지 인쇄 (인쇄 브랜드 상품 포함 시, 주문당 1건)
  paymentDetail: {
    paymentByCash: number;
    taxFreeAmount?: number;
    taxableAmount?: number;
    vatAmount?: number;
    paymentByPoint: number;
  };
  webBaseUrl?: string; // 프론트 도메인 URL (백엔드 문자 발송 시 사용)
}

export interface GiftOrderUpdate {
  _id: string;
  docStatus?: GiftOrderDocStatus;
  orderDetails?: {
    giftItem: string;
    deliveryDetail?: {
      status?: GiftOrderDeliveryStatus;
      deliveredAt?: Date;
      deliveredImage?: string;
    };
  }[];
  paymentDetail?: {
    status?: GiftOrderPaymentStatus;
    cancelReason?: string;
  };
}

// 가상계좌·계좌 입금 건은 부트페이 자동취소가 불가하므로, 관리자가 실제로
// 이체한 환불 내역(금액·이체일·메모)을 함께 넘겨 서버가 수동 환불 처리한다.
// 계좌번호(고객 개인정보)는 받지 않는다.
export interface ManualRefundInput {
  refundAmount: number; // 환불(이체) 금액
  transferDate: string; // 이체일 (YYYY-MM-DD)
  memo?: string; // 이체한 우리 통장 등 비고
}

// 서버가 수기환불 취소 성공 시 paymentDetail.manualRefunds[]에 append하는 기록.
// (조회용 — 전체취소는 1건, 부분취소를 여러 번 하면 여러 건 누적. spec §7)
export interface GiftOrderManualRefund {
  cancelId?: string;
  refundAmount: number;
  refundTaxFree?: number;
  transferDate: string; // YYYY-MM-DD
  memo?: string;
  cancelReason?: string;
  couponIds?: string[]; // 부분취소 대상 (전체취소는 빈 배열)
  deliveryIds?: string[];
  refundedBy?: string; // 처리한 관리자 _id
  refundedAt?: Date;
}

export interface GiftOrderCancel {
  _id: string;
  cancelReason: string;
  // 가상계좌/입금 결제 전체취소 시에만 동봉 (카드류는 넘기지 않는다)
  manualRefund?: ManualRefundInput;
}

export interface GiftOrderPartialCancel {
  _id: string;
  cancelReason: string;
  couponIds: string[];
  deliveryIds: string[];
  // 가상계좌/입금 결제 부분취소 시에만 동봉 (카드류는 넘기지 않는다)
  manualRefund?: ManualRefundInput;
}

export interface GiftOrderPartialCancelResponse {
  success: boolean;
  cancelledPrice: number;
}

// 답례글 업데이트 DTO
export interface GiftOrderCourtesyUpdate {
  courtesyContent: string;
}

export type GiftStoreRole =
  | LowerRole.Admin
  | LowerRole.StoreCustomer
  | LowerRole.StoreGuest
  | LowerRole.Public;
